/**
 * The `dm.policy` closed vocabularies, spelled once for the api, the CLI and the web
 * (DM #2382). A LEAF: no import and no re-export (`__tests__/dmPolicyVocabulary.test.ts`) —
 * the web launcher chain loads it through the subpath (web `dmPolicyWire.test.ts`). The
 * gate/kernel enforcer maps (repo file paths) stay in the api's `dmPolicyPayload.ts`.
 */
/** DM's process roles, harness-neutral; a harness's agent names map onto them in its adapter. */
export const DM_ROLE_VALUES = [
    'orchestrator',
    'explorer',
    'planner',
    'implementer',
    'tester',
    'reviewer',
    'shipper',
    'platform-agent',
    'test-runner',
];
export const DM_POLICY_ROLE_VALUES = DM_ROLE_VALUES;
/** The harness-neutral hook vocabulary; only `before_tool` is enforced today. */
export const DM_HARNESS_EVENT_VALUES = [
    'before_tool',
    'after_tool',
    'session_start',
    'prompt',
    'subagent_start',
    'subagent_stop',
    'stop',
    'session_end',
];
export const DM_POLICY_HOOK_EVENT = 'before_tool';
export const DM_POLICY_HOOK_TOOL_VALUES = ['shell', 'edit', 'read', 'web', 'agent'];
export const DM_POLICY_STATUS_VALUES = ['draft', 'active', 'retired'];
export const DM_POLICY_KIND_VALUES = ['rule', 'definition', 'method', 'checklist'];
export const DM_POLICY_ENFORCEMENT_VALUES = ['locked', 'default', 'advisory'];
export const DM_POLICY_CHECK_TYPE_VALUES = ['hook', 'gate', 'manual', 'kernel'];
/** A hook check's verdict (absent = deny); only default/advisory may `ask` (W4-H2 S4). */
export const DM_POLICY_HOOK_DECISION_VALUES = ['deny', 'ask'];
/** W8c — who can fix a default deny: `agent` = deny + hint, no inbox item; absent = `human` (the grant path). */
export const DM_POLICY_HOOK_CORRECTABLE_VALUES = ['agent', 'human'];
/** W8d-4 — the most classes a default deny's `check.class_grantable` opts in (single-use, work-unit grants). */
export const DM_POLICY_CLASS_GRANTABLE_MAX = 8;
/** W8c — how the guard matched a Bash call: at command position (the mask read it), or as full text (it could not). */
export const DM_POLICY_GUARD_MATCHERS = ['position', 'fulltext_fallback'];
/** W8c — why the mask fell back (`dm-policy-guard.sh` writes no other; parity-pinned by dmCliHookPolicyDeny.test.ts). */
export const DM_POLICY_GUARD_FALLBACK_REASONS = [
    'too_long',
    'unbalanced_quote',
    'unclosed_nesting',
    'heredoc_unterminated',
    'heredoc_tag',
    'mask_timeout',
    'mask_error',
];
/** W8c — a ledger line's matched fragment: the redacted hit, ≤160 code points (a cut ends in `…`). */
export const DM_POLICY_GUARD_FRAGMENT_MAX = 160;
export const DM_POLICY_APPLIES_TO_ALL = 'all';
/** Every non-root context: each role but `orchestrator`, an unresolved one included. */
export const DM_POLICY_APPLIES_TO_SUBAGENTS = 'subagents';
/** Closed pseudo-values; each stands alone in `applies_to`. */
export const DM_POLICY_APPLIES_TO_PSEUDO = [DM_POLICY_APPLIES_TO_ALL, DM_POLICY_APPLIES_TO_SUBAGENTS];
/** The closed `check.gate` vocabulary: root `package.json` scripts (enforcer map: api). */
export const DM_POLICY_GATE_NAMES = [
    'test:guards',
    'lint:gate',
    'knip:gate',
    'drift:gate',
    'tsc-test:gate:api',
    'tsc-test:gate:web',
    'tsc-test:gate:scripts',
    'check:no-mysql2',
    'check:pg-alias',
    'test:scripts',
    'harness:lint',
    'boundary:gate',
];
/** The closed `check.enforced_by` vocabulary of a `kernel` check (enforcer map: api). */
export const DM_POLICY_KERNEL_NAMES = [
    'dm-id-notation',
    'requires_ac_closure',
    'boundary-binding',
    // W5-4: kernel gates live since W2b / CAPS T1 / G1; the names let their policies declare check.type=kernel.
    'spec-plan-gate',
    'dm-content-cap',
    'waiver-human-only',
    // T1-K1 (#3961): the topic gate, the type→parent matrix, the hierarchy chain rules (area_depth/wave_depth/epic_root_area).
    'requires_topic_edge',
    'dm-type-parent-matrix',
    'dm-hierarchy-chain',
    // T2-1: dm.plan draft→approved requires_field data.conflict_check + the shape at the write seams.
    'plan-conflict-check',
];
export const DM_POLICY_KEY_MAX = 64;
export const DM_POLICY_KEY_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
//# sourceMappingURL=dmPolicyVocabulary.js.map